"use client"

import Image from "next/image"
import Link from "next/link"
import type { Route } from "next"
import type {
  ComponentProps,
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  ReactNode,
} from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslations } from "next-intl"
import type { FragmentOf } from "@/lib/legacy-fragment-types"
import type { EnrichedMediaItem } from "@/lib/enrichment"
import { enrichMediaItem } from "@/lib/enrichment"
import {
  CONTENT_WIDTH_ALIGN_CLASSES,
  WATCH_PAGE_CONTENT_CLASSES,
} from "@/lib/content-width"
import type { RouteVideo } from "@/lib/content"
import { mediaCollectionFragment } from "@/lib/fragments/media-collection"
import { MuxHoverPreview } from "@/components/watch/MuxHoverPreview"
import {
  WATCH_BASE_PATH,
  asLocaleSlug,
  tryAsContentSlug,
  tryAsLocaleSlug,
  videosIndexPath,
  watchVideoPath,
} from "@/lib/routes"
import { WatchProgressBar } from "@/components/watch/WatchProgressBar"
import { resolveMediaImageUrl } from "@/lib/media-image-url"
import { hexToRgb, readableScrimRgb } from "@/lib/readable-scrim-color"
import { resolveMuxAnimatedPreviewUrl } from "@/lib/url"
import { cn } from "@/lib/utils"
import { normalizeWatchRootHref } from "@/lib/watch-paths"
import { resolveWatchShareUrlFromPathname } from "@/lib/share"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel"
import {
  VIDEO_THUMBNAIL_FOCUS_TARGET_CLASS,
  VideoThumbnailInteractionFrame,
} from "@/components/ui/video-thumbnail-interaction-frame"
import {
  VideoThumbnailCaption,
  VideoThumbnailEyebrow,
  VideoThumbnailTitle,
} from "@/components/ui/video-thumbnail-caption"
import {
  WATCH_IMMERSIVE_BACKGROUND_BRIGHTNESS_CLASS,
  WATCH_IMMERSIVE_BACKGROUND_COLOR,
  WATCH_IMMERSIVE_BACKGROUND_SATURATION_CLASS,
  WATCH_MEDIA_SECTION_VERTICAL_PADDING_CLASS,
} from "@/components/watch/watch-section-styles"

// Hoisted so the throwing constructor runs once at module load, not per card.
const DEFAULT_COLLECTION_LOCALE = asLocaleSlug("english")

export { mediaCollectionFragment }

type MediaCollectionProps = {
  data: FragmentOf<typeof mediaCollectionFragment>
  routeVideo?: RouteVideo | null
  languageSlug?: string | null
  initialSelectedSnap?: number
  onSelectedSnapChange?: (snap: number) => void
}

type MediaCollectionCarouselApi = Parameters<
  NonNullable<ComponentProps<typeof Carousel>["setApi"]>
>[0]

type HoverBackdropLayer = {
  id: number
  imageUrl: string
  state: "entering" | "exiting"
}

// Values live in `watch-section-styles` so the /videos collection sidebars
// render the same immersive backdrop as these authored sections.
const IMMERSIVE_BACKGROUND_SATURATION_CLASS =
  WATCH_IMMERSIVE_BACKGROUND_SATURATION_CLASS
const IMMERSIVE_BACKGROUND_BRIGHTNESS_CLASS =
  WATCH_IMMERSIVE_BACKGROUND_BRIGHTNESS_CLASS
const IMMERSIVE_BACKGROUND_COLOR = WATCH_IMMERSIVE_BACKGROUND_COLOR

// Phone column widths are set by what the card's TEXT needs, not by how many
// cards fit: at the previous 56%/34% a 390px viewport rendered 185px and 113px
// cards, and 7 of 8 poster titles were already clamped away at 14px. The wider
// phone columns below are what let `video-thumbnail-caption` raise its phone
// tier without trading a too-small title for a truncated one. `imageSizes` must
// move with `columns` — it is the `sizes` attribute behind these same cards.
const MOBILE_CAROUSEL_LAYOUT = {
  horizontal: {
    columns: "auto-cols-[72%] gap-4 sm:auto-cols-[42%] md:gap-5",
    imageSizes: "(max-width: 639.98px) 72vw, (max-width: 767px) 42vw, 360px",
  },
  vertical: {
    columns: "auto-cols-[46%] gap-3 sm:auto-cols-[26%] md:gap-4",
    imageSizes: "(max-width: 639.98px) 46vw, (max-width: 767px) 26vw, 220px",
  },
} as const

function backgroundImageStyle(imageUrl: string | null) {
  return imageUrl ? { backgroundImage: `url("${imageUrl}")` } : undefined
}

function alphaColor(color: string, opacity: number): string {
  const rgb = hexToRgb(color)
  if (!rgb) return color

  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`
}

function textScrimStyle(
  item: EnrichedMediaItem,
  orientation: "horizontal" | "vertical",
): CSSProperties {
  const rgb = readableScrimRgb(item.dominantColor)
  if (!rgb) {
    return {
      background:
        "linear-gradient(to top, rgba(0,0,0,.78), rgba(0,0,0,.18), transparent)",
    }
  }

  if (orientation === "vertical") {
    return {
      background: `linear-gradient(to top, rgb(${rgb.r},${rgb.g},${rgb.b}), rgba(${rgb.r},${rgb.g},${rgb.b},.78) 36%, rgba(${rgb.r},${rgb.g},${rgb.b},.22) 68%, transparent 100%)`,
    }
  }

  return {
    background: `linear-gradient(to top, rgb(${rgb.r},${rgb.g},${rgb.b}), rgba(${rgb.r},${rgb.g},${rgb.b},.86) 24%, rgba(${rgb.r},${rgb.g},${rgb.b},.28) 42%, transparent 62%)`,
  }
}

function tintOverlayStyle(isRail: boolean): CSSProperties {
  const strong = alphaColor(IMMERSIVE_BACKGROUND_COLOR, isRail ? 0.92 : 0.86)
  const soft = alphaColor(IMMERSIVE_BACKGROUND_COLOR, isRail ? 0.38 : 0.34)
  const deep = alphaColor("#050505", isRail ? 0.62 : 0.76)

  return {
    background: `linear-gradient(to top right, ${deep}, ${soft} 42%, ${strong})`,
  }
}

function backdropLayerStyle(
  imageUrl: string | null,
  opacity: string,
  extraStyle?: CSSProperties,
): CSSProperties {
  return {
    ...backgroundImageStyle(imageUrl),
    ...extraStyle,
    "--watch-home-backdrop-opacity": opacity,
  } as CSSProperties
}

export function MediaCollection({
  data,
  routeVideo,
  languageSlug,
  initialSelectedSnap,
  onSelectedSnapChange,
}: MediaCollectionProps) {
  const {
    id,
    title,
    subtitle,
    mediaDescription: description,
    categoryLabel,
    mediaCtaLink: ctaLink,
    mediaCtaLabel: rawCtaLabel,
    mediaDefaultCollectionSlug,
    showItemNumbers,
    mediaCollectionVariant: variant,
    itemsSource,
    footerText: rawFooterText,
    items,
  } = data
  const thumbnailOrientation = (
    data as typeof data & {
      thumbnailOrientation?: "vertical" | "horizontal" | null
    }
  ).thumbnailOrientation

  const ctaLabel = typeof rawCtaLabel === "string" ? rawCtaLabel : null
  const footerText = typeof rawFooterText === "string" ? rawFooterText : null
  const selectedSource = itemsSource ?? "manual"
  const rawEnrichedItems: Array<EnrichedMediaItem | null | undefined> =
    selectedSource === "routeVideoChildren"
      ? (routeVideo?.relatedItems ?? [])
      : (items ?? []).map(enrichMediaItem)
  const enrichedItems = rawEnrichedItems.filter(
    (item): item is EnrichedMediaItem => item != null,
  )
  const resolvedLanguageSlug =
    tryAsLocaleSlug(languageSlug ?? "") ?? DEFAULT_COLLECTION_LOCALE
  const inferredCollectionSlug =
    selectedSource === "routeVideoChildren"
      ? tryAsContentSlug(routeVideo?.slug ?? "")
      : tryAsContentSlug(mediaDefaultCollectionSlug ?? "")
  const inferredCtaLink = inferredCollectionSlug
    ? `${WATCH_BASE_PATH}${watchVideoPath(inferredCollectionSlug, resolvedLanguageSlug)}`
    : null
  const explicitCtaLink =
    typeof ctaLink === "string" && ctaLink.trim().length > 0 ? ctaLink : null

  if (
    process.env.NODE_ENV === "development" &&
    selectedSource === "routeVideoChildren" &&
    routeVideo == null
  ) {
    console.warn(
      "[MediaCollection] routeVideoChildren source requires routeVideo context.",
    )
  }

  if (enrichedItems.length === 0) return null

  return (
    <WatchHomeMediaCollection
      id={id}
      categoryLabel={categoryLabel}
      title={title}
      subtitle={subtitle}
      description={description}
      ctaLink={explicitCtaLink ?? inferredCtaLink}
      ctaLabel={ctaLabel}
      footerText={footerText}
      variant={variant}
      thumbnailOrientation={thumbnailOrientation ?? null}
      showItemNumbers={showItemNumbers}
      items={enrichedItems}
      fallbackLanguageSlug={resolvedLanguageSlug}
      initialSelectedSnap={initialSelectedSnap}
      onSelectedSnapChange={onSelectedSnapChange}
    />
  )
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      focusable="false"
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.493 1.607c.845-.395 1.799-.187 2.555.292l.03.02 11.017 8.111c.781.464 1.504 1.175 1.505 2.172.001.99-.715 1.734-1.496 2.246l-10.93 7.644-.046.025c-.788.437-1.762.706-2.63.242-.879-.47-1.193-1.442-1.25-2.387l-.001-.028L4.2 3.968c-.019-1.02.395-1.943 1.292-2.361Z"
      />
    </svg>
  )
}

function WatchHomeMediaCollection({
  id,
  categoryLabel,
  title,
  subtitle,
  description,
  ctaLink,
  ctaLabel,
  footerText,
  variant,
  thumbnailOrientation,
  showItemNumbers,
  items,
  fallbackLanguageSlug,
  initialSelectedSnap,
  onSelectedSnapChange,
}: {
  id: string
  categoryLabel: string | null
  title: string | null
  subtitle: string | null
  description: string | null
  ctaLink: string | null
  ctaLabel: string | null
  footerText: string | null
  variant: string | null
  thumbnailOrientation: "vertical" | "horizontal" | null
  showItemNumbers: boolean | null
  items: EnrichedMediaItem[]
  fallbackLanguageSlug: ReturnType<typeof asLocaleSlug>
  initialSelectedSnap?: number
  onSelectedSnapChange?: (snap: number) => void
}) {
  const t = useTranslations("WatchHome")
  const isRail = variant === "carousel"
  const orientation =
    thumbnailOrientation ??
    (isRail || variant === "collection" ? "vertical" : "horizontal")
  const isVertical = orientation === "vertical"
  const usesMobileCarousel = !isRail && items.length > 1
  const needsKeyboardScrollableCarousel =
    usesMobileCarousel &&
    items.some(
      (item) => !item.videoSlug || tryAsContentSlug(item.videoSlug) == null,
    )
  const desktopGridColumns = isVertical
    ? "md:grid-cols-4 xl:grid-cols-4"
    : variant === "hero" || variant === "player"
      ? "md:grid-cols-2"
      : "md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
  const mobileCarouselLayout = isVertical
    ? MOBILE_CAROUSEL_LAYOUT.vertical
    : MOBILE_CAROUSEL_LAYOUT.horizontal
  const staticGridColumns = isVertical
    ? "grid-cols-2 gap-4"
    : "grid-cols-1 gap-5"
  const defaultBackgroundUrl =
    items.map(mediaItemBackdropImageUrl).find((imageUrl) => imageUrl) ?? null
  const latestHoveredBackgroundUrlRef = useRef<string | null>(null)
  const hoverLayerIdRef = useRef(0)
  const [isSectionActive, setIsSectionActive] = useState(false)
  const [settledBackgroundUrl, setSettledBackgroundUrl] = useState<
    string | null
  >(defaultBackgroundUrl)
  const [hoverBackdropLayers, setHoverBackdropLayers] = useState<
    HoverBackdropLayer[]
  >([])
  const [carouselApi, setCarouselApi] = useState<MediaCollectionCarouselApi>()
  const selectedSnapRef = useRef(initialSelectedSnap ?? 0)
  const normalizedCtaLink = normalizeWatchRootHref(ctaLink)
  const standaloneCtaUrl = normalizedCtaLink?.startsWith(`${WATCH_BASE_PATH}/`)
    ? resolveWatchShareUrlFromPathname({
        origin: "https://www.jesusfilm.org",
        pathname: normalizedCtaLink,
      })
    : null
  const watchHref = standaloneCtaUrl
    ? new URL(standaloneCtaUrl).pathname
    : (normalizedCtaLink ?? `${WATCH_BASE_PATH}${videosIndexPath()}`)
  const tintStyle = tintOverlayStyle(isRail)
  const titleRowStart = categoryLabel ? "row-start-2" : "row-start-1"
  const watchCta = (
    <a
      href={watchHref}
      data-testid="media-collection-cta"
      className={cn(
        "inline-flex w-fit shrink-0 items-center gap-2 rounded-full bg-white px-4 py-2 text-sm sm:text-xs font-bold tracking-wider text-black uppercase transition-colors hover:bg-red-500 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white",
        title && `col-start-2 ${titleRowStart}`,
      )}
    >
      <PlayIcon />
      {ctaLabel ?? t("watch")}
    </a>
  )
  const categoryEyebrow = categoryLabel ? (
    <p className="text-sm font-semibold tracking-eyebrow text-red-100/60 uppercase sm:text-xs xl:text-sm 2xl:text-base">
      {categoryLabel}
    </p>
  ) : null
  const supportingCopy = (
    <>
      {subtitle ? (
        <p
          data-testid="media-collection-supporting-title"
          className={cn(
            "w-full text-lg leading-snug font-normal text-stone-100/90 xl:text-xl",
            title && "pt-1",
          )}
        >
          {subtitle}
        </p>
      ) : null}
      {description ? (
        <p
          data-testid="media-collection-description"
          className="w-full pt-2 text-base leading-relaxed font-normal text-stone-200/80 sm:text-sm xl:text-base"
        >
          {description}
        </p>
      ) : null}
    </>
  )

  function updateHoverBackground(imageUrl: string | null) {
    if (imageUrl) {
      latestHoveredBackgroundUrlRef.current = imageUrl
    }

    setHoverBackdropLayers((layers) => {
      let currentLayer: HoverBackdropLayer | null = null
      for (let index = layers.length - 1; index >= 0; index -= 1) {
        if (layers[index]?.state === "entering") {
          currentLayer = layers[index]
          break
        }
      }
      if ((currentLayer?.imageUrl ?? null) === imageUrl) return layers

      const exitingLayers = layers.map((layer) => ({
        ...layer,
        state: "exiting" as const,
      }))

      if (!imageUrl) return exitingLayers

      hoverLayerIdRef.current += 1
      return [
        ...exitingLayers,
        {
          id: hoverLayerIdRef.current,
          imageUrl,
          state: "entering",
        },
      ]
    })
  }

  function settleLatestHoveredBackground() {
    setSettledBackgroundUrl(
      latestHoveredBackgroundUrlRef.current ?? defaultBackgroundUrl,
    )
  }

  useEffect(() => {
    if (hoverBackdropLayers.length === 0) return undefined

    const timeoutId = window.setTimeout(() => {
      setSettledBackgroundUrl(
        latestHoveredBackgroundUrlRef.current ?? defaultBackgroundUrl,
      )
      setHoverBackdropLayers([])
    }, 1250)

    return () => window.clearTimeout(timeoutId)
  }, [defaultBackgroundUrl, hoverBackdropLayers])

  useEffect(() => {
    if (!carouselApi) return

    const reportSelection = () => {
      const selectedSnap = carouselApi.selectedScrollSnap()
      selectedSnapRef.current = selectedSnap
      onSelectedSnapChange?.(selectedSnap)
    }
    const restoreSelection = () => {
      const lastSnap = carouselApi.scrollSnapList().length - 1
      if (lastSnap < 0) return
      const clampedSnap = Math.min(
        Math.max(Math.trunc(selectedSnapRef.current), 0),
        lastSnap,
      )
      selectedSnapRef.current = clampedSnap
      if (carouselApi.selectedScrollSnap() !== clampedSnap) {
        carouselApi.scrollTo(clampedSnap, true)
      }
      onSelectedSnapChange?.(clampedSnap)
    }

    restoreSelection()
    carouselApi.on("reInit", restoreSelection)
    carouselApi.on("select", reportSelection)
    return () => {
      carouselApi.off("reInit", restoreSelection)
      carouselApi.off("select", reportSelection)
    }
  }, [carouselApi, onSelectedSnapChange])

  return (
    <section
      id={id}
      data-testid="media-collection-section"
      onPointerEnter={() => setIsSectionActive(true)}
      onPointerLeave={() => {
        settleLatestHoveredBackground()
        setIsSectionActive(false)
        updateHoverBackground(null)
      }}
      onFocus={() => setIsSectionActive(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          settleLatestHoveredBackground()
          setIsSectionActive(false)
          updateHoverBackground(null)
        }
      }}
      className={cn(
        "scroll-mt-24 relative overflow-hidden text-white",
        WATCH_MEDIA_SECTION_VERTICAL_PADDING_CLASS,
      )}
      style={{ backgroundColor: IMMERSIVE_BACKGROUND_COLOR }}
    >
      {settledBackgroundUrl ? (
        <div
          data-testid="media-collection-default-backdrop"
          className={cn(
            "absolute inset-0 z-0 scale-105 bg-cover bg-center bg-no-repeat opacity-100 blur-2xl",
            IMMERSIVE_BACKGROUND_BRIGHTNESS_CLASS,
            IMMERSIVE_BACKGROUND_SATURATION_CLASS,
          )}
          style={backgroundImageStyle(settledBackgroundUrl)}
          aria-hidden
        />
      ) : null}
      {hoverBackdropLayers.map((layer) => (
        <div
          key={`media-collection-hover-backdrop-${layer.id}`}
          data-testid={
            layer.state === "entering"
              ? "media-collection-hover-backdrop"
              : "media-collection-hover-backdrop-previous"
          }
          className={cn(
            "absolute inset-0 z-0 scale-105 bg-cover bg-center bg-no-repeat blur-2xl",
            layer.state === "entering"
              ? "watch-home-section-backdrop-enter"
              : "watch-home-section-backdrop-exit",
            IMMERSIVE_BACKGROUND_BRIGHTNESS_CLASS,
            IMMERSIVE_BACKGROUND_SATURATION_CLASS,
          )}
          style={backdropLayerStyle(layer.imageUrl, "1")}
          aria-hidden
        />
      ))}
      <div
        aria-hidden
        data-testid="media-collection-tint"
        className={cn(
          "absolute inset-0 z-[1] transition-opacity duration-500 ease-out",
          IMMERSIVE_BACKGROUND_BRIGHTNESS_CLASS,
          IMMERSIVE_BACKGROUND_SATURATION_CLASS,
          isSectionActive ? "opacity-0" : "opacity-100",
          "mix-blend-multiply",
        )}
        style={tintStyle}
      />
      <div
        aria-hidden
        className={cn(
          "absolute inset-0 z-[1] bg-[url(/watch/images/overlay.svg)] bg-repeat mix-blend-multiply transition-opacity duration-500 ease-out",
          isSectionActive ? "opacity-0" : isRail ? "opacity-85" : "opacity-65",
        )}
      />

      <div className={cn("relative z-[3] pb-6", WATCH_PAGE_CONTENT_CLASSES)}>
        <div className="flex flex-col gap-1">
          {title ? (
            <>
              <div
                data-testid="media-collection-title-row"
                className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4 gap-y-1"
              >
                {categoryEyebrow}
                <h2
                  className={cn(
                    "col-start-1 max-w-4xl text-2xl leading-tight font-bold tracking-normal text-white xl:text-3xl 2xl:text-4xl",
                    titleRowStart,
                  )}
                >
                  {title}
                </h2>
                {watchCta}
              </div>
              {supportingCopy}
            </>
          ) : (
            <>
              {categoryEyebrow}
              <div
                data-testid="media-collection-titleless-layout"
                className="flex flex-col gap-6 lg:items-end"
              >
                <div
                  data-testid="media-collection-titleless-supporting-copy"
                  className="flex w-full flex-col gap-1"
                >
                  {supportingCopy}
                </div>
                {watchCta}
              </div>
            </>
          )}
        </div>
      </div>

      {isRail ? (
        <div className={cn("relative z-[3]", CONTENT_WIDTH_ALIGN_CLASSES)}>
          <Carousel
            aria-label={title ?? t("mediaCollection")}
            data-testid="media-collection-carousel"
            opts={{
              align: "start",
              dragFree: true,
              containScroll: "trimSnaps",
              ...(initialSelectedSnap == null
                ? null
                : { startIndex: Math.max(Math.trunc(initialSelectedSnap), 0) }),
              watchDrag: (api) => api.scrollSnapList().length > 1,
            }}
            setApi={
              initialSelectedSnap == null && onSelectedSnapChange == null
                ? undefined
                : setCarouselApi
            }
            className="w-full"
          >
            <CarouselContent
              data-testid="media-collection-carousel-content"
              className="-ml-5 pl-5 md:pl-16 xl:pl-24"
            >
              {items.map((item: EnrichedMediaItem, index: number) => (
                <CarouselItem
                  key={`${item.id}-${index}`}
                  data-testid="media-collection-carousel-item"
                  className={cn(
                    "py-1 pl-5",
                    isVertical ? "max-w-[200px]" : "max-w-[360px]",
                  )}
                >
                  <VideoCard
                    item={item}
                    index={index}
                    orientation={orientation}
                    showItemNumbers={showItemNumbers}
                    fallbackLanguageSlug={fallbackLanguageSlug}
                    onHover={() =>
                      updateHoverBackground(mediaItemBackdropImageUrl(item))
                    }
                  />
                </CarouselItem>
              ))}
              <CarouselItem
                aria-hidden="true"
                tabIndex={-1}
                data-testid="media-collection-carousel-end-spacer"
                className="basis-auto pl-0"
              >
                <div className="w-5 md:w-16 xl:w-24" />
              </CarouselItem>
            </CarouselContent>
            <CarouselPrevious label={t("previousVideoPreview")} />
            <CarouselNext label={t("nextVideoPreview")} />
          </Carousel>
        </div>
      ) : (
        <div
          data-testid={
            usesMobileCarousel ? "media-collection-mobile-carousel" : undefined
          }
          role={usesMobileCarousel ? "region" : undefined}
          tabIndex={needsKeyboardScrollableCarousel ? 0 : undefined}
          aria-label={
            usesMobileCarousel ? (title ?? t("mediaCollection")) : undefined
          }
          className={cn(
            "relative z-[3]",
            usesMobileCarousel
              ? // scroll-pl-5 mirrors the inner grid's px-5: without it, mandatory
                // snapping aligns the first card's snap-start edge to the bare
                // scrollport on load, pulling scrollLeft past the content inset so
                // the first card sits flush with the viewport edge. Snapping is
                // mobile-only (md:snap-none), so only the px-5 tier needs a mirror.
                `${CONTENT_WIDTH_ALIGN_CLASSES} snap-x snap-mandatory scroll-pl-5 overflow-x-auto overscroll-x-contain scroll-smooth [scrollbar-width:none] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/80 md:snap-none md:overflow-visible [&::-webkit-scrollbar]:hidden`
              : WATCH_PAGE_CONTENT_CLASSES,
          )}
        >
          <div
            data-testid="media-collection-grid"
            className={cn(
              "grid",
              usesMobileCarousel
                ? "grid-flow-col px-5 pb-1 md:grid-flow-row md:auto-cols-auto md:px-16 xl:px-24"
                : null,
              usesMobileCarousel
                ? mobileCarouselLayout.columns
                : staticGridColumns,
              desktopGridColumns,
            )}
          >
            {items.map((item: EnrichedMediaItem, index: number) => (
              <VideoCard
                key={`${item.id}-${index}`}
                item={item}
                index={index}
                orientation={orientation}
                showItemNumbers={showItemNumbers}
                fallbackLanguageSlug={fallbackLanguageSlug}
                compactImageSizes={
                  usesMobileCarousel
                    ? mobileCarouselLayout.imageSizes
                    : undefined
                }
                className={usesMobileCarousel ? "snap-start" : undefined}
                onHover={() =>
                  updateHoverBackground(mediaItemBackdropImageUrl(item))
                }
              />
            ))}
          </div>
        </div>
      )}

      {footerText ? (
        <div className={cn("relative z-[3]", WATCH_PAGE_CONTENT_CLASSES)}>
          <p
            data-testid="media-collection-footer"
            className="mt-8 max-w-5xl text-sm leading-relaxed font-normal text-stone-200/80 sm:text-xs xl:text-sm"
          >
            {footerText}
          </p>
        </div>
      ) : null}
    </section>
  )
}

function mediaItemDisplayImageUrl(item: EnrichedMediaItem) {
  return item.imageUrl
}

function mediaItemBackdropImageUrl(item: EnrichedMediaItem) {
  return item.blurDataUrl ?? item.imageUrl
}

/**
 * The card's outer element. A routable card is a `next/link` so the click is a
 * client-side navigation — a raw anchor here tears the document down and
 * re-executes the whole bundle before anything paints. A card with no
 * destination stays a plain `div` with no handlers.
 *
 * Mirrors `WatchHomeCard`'s `CardFrame`, which already solves this split.
 */
function CardFrame({
  href,
  prefetch,
  className,
  ariaLabel,
  onPointerMove,
  onFocus,
  style,
  children,
}: {
  /** Base-path-relative; `next/link` prepends `/watch`. */
  href: string | undefined
  prefetch: boolean | undefined
  className: string
  ariaLabel: string | undefined
  onPointerMove: ((event: ReactPointerEvent<HTMLElement>) => void) | undefined
  onFocus: (() => void) | undefined
  style?: CSSProperties
  children: ReactNode
}) {
  if (href == null) {
    return (
      <div className={className} data-testid="VideoCard" style={style}>
        {children}
      </div>
    )
  }

  return (
    <Link
      // Built by `watchVideoPath`, which returns a typed base-path-relative
      // Route; the cast covers the enriched-item slug widening only.
      href={href as Route}
      prefetch={prefetch}
      className={className}
      aria-label={ariaLabel}
      data-testid="VideoCard"
      // Surfaced so a test can assert the production URL shape without a
      // configured basePath, the way SiblingCarousel does.
      data-href={href}
      onPointerMove={onPointerMove}
      onFocus={onFocus}
      style={style}
    >
      {children}
    </Link>
  )
}

function VideoCard({
  item,
  index,
  orientation,
  showItemNumbers,
  fallbackLanguageSlug,
  compactImageSizes,
  className,
  onHover,
}: {
  item: EnrichedMediaItem
  index: number
  orientation: "horizontal" | "vertical"
  showItemNumbers: boolean | null
  fallbackLanguageSlug: ReturnType<typeof asLocaleSlug>
  compactImageSizes?: string
  className?: string
  onHover?: () => void
}) {
  const t = useTranslations("WatchHome")
  // `next/link` prepends the `/watch` basePath itself, so this stays
  // base-path-relative — hand-prefixing it here renders `/watch/watch/...`.
  // Prefer the resolved item dub language; fall back to the current page
  // language for route-derived items or legacy payloads.
  const slug = item.videoSlug ? tryAsContentSlug(item.videoSlug) : null
  const itemLanguageSlug = tryAsLocaleSlug(item.languageSlug ?? "")
  const cardLanguageSlug =
    itemLanguageSlug ?? fallbackLanguageSlug ?? DEFAULT_COLLECTION_LOCALE
  const href = slug ? watchVideoPath(slug, cardLanguageSlug) : undefined
  const isInteractive = Boolean(href)
  // Prefetch stays off until the viewer shows intent. `/watch` is a windowed
  // infinite feed, so viewport-eligible prefetch across every mounted card
  // would fan out into on-demand origin renders for slugs nobody asked for.
  const [prefetchArmed, setPrefetchArmed] = useState(false)
  const armPrefetch = useCallback(() => {
    setPrefetchArmed(true)
  }, [])
  // `pointerenter` also fires when the feed scrolls under a still pointer,
  // which is not intent; require actual movement over the card. Movement is
  // only an intent signal for a hovering pointer, though — a touch scroll
  // emits `pointermove` across every card it drags past, which would arm the
  // whole rail. Touch users still get the focus path and the click itself.
  const handlePointerMove = isInteractive
    ? (event: ReactPointerEvent<HTMLElement>) => {
        if (event.pointerType !== "mouse") return
        armPrefetch()
        onHover?.()
      }
    : undefined
  const handleFocus = isInteractive
    ? () => {
        armPrefetch()
        onHover?.()
      }
    : undefined
  const imageSrc = resolveMediaImageUrl(mediaItemDisplayImageUrl(item))
  const blurDataUrl = item.blurDataUrl ?? undefined
  const muxPreviewUrl = resolveMuxAnimatedPreviewUrl(item.muxPlaybackId)
  const isVertical = orientation === "vertical"
  const compactOnMobile = compactImageSizes != null
  const compactFrameSizeClasses = isVertical
    ? "aspect-[2/3] min-h-0 md:min-h-[16rem]"
    : "aspect-video min-h-0 md:min-h-[10rem]"
  const defaultFrameSizeClasses = isVertical
    ? "aspect-[2/3] min-h-[13rem] sm:min-h-[16rem]"
    : "aspect-video min-h-[10rem]"
  const frameSizeClasses = compactOnMobile
    ? compactFrameSizeClasses
    : defaultFrameSizeClasses
  const defaultImageSizes = isVertical
    ? "(max-width: 768px) 46vw, 220px"
    : "(max-width: 768px) 100vw, 360px"
  const responsiveImageSizes = compactImageSizes ?? defaultImageSizes
  const [isMuxPreviewLoaded, setIsMuxPreviewLoaded] = useState(false)
  const accessibleTitle =
    item.title || [item.label, item.videoSlug].filter(Boolean).join(" ")

  return (
    <CardFrame
      href={href}
      prefetch={prefetchArmed ? undefined : false}
      className={cn(
        "relative block overflow-hidden rounded-lg bg-black text-inherit no-underline shadow-[0_2px_6px_rgba(0,0,0,0.35),0_14px_32px_-12px_rgba(0,0,0,0.6)] transition-[opacity,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
        isInteractive
          ? "group cursor-pointer hover:shadow-[0_4px_10px_rgba(0,0,0,0.4),0_22px_44px_-14px_rgba(0,0,0,0.7)] active:scale-[0.985] active:opacity-90 motion-reduce:active:scale-100"
          : "cursor-default",
        isInteractive && VIDEO_THUMBNAIL_FOCUS_TARGET_CLASS,
        className,
      )}
      ariaLabel={
        isInteractive ? t("showVideo", { title: accessibleTitle }) : undefined
      }
      onPointerMove={handlePointerMove}
      onFocus={handleFocus}
    >
      <div
        className={cn(
          "relative w-full overflow-hidden rounded-lg bg-black/50",
          frameSizeClasses,
        )}
      >
        {imageSrc ? (
          <Image
            src={imageSrc}
            alt={item.title}
            fill
            sizes={responsiveImageSizes}
            placeholder={blurDataUrl ? "blur" : "empty"}
            blurDataURL={blurDataUrl}
            className="poster-hover-zoom object-cover"
            style={
              isVertical || item.dominantColor
                ? { objectPosition: isVertical ? "center" : "left top" }
                : {
                    objectPosition: "left top",
                    maskImage:
                      "linear-gradient(to top, transparent 0%, rgba(0,0,0,.4) 30%, black 42%)",
                    WebkitMaskImage:
                      "linear-gradient(to top, transparent 0%, rgba(0,0,0,.4) 30%, black 42%)",
                  }
            }
          />
        ) : (
          <div
            aria-hidden
            className="h-full w-full bg-[linear-gradient(135deg,#111827,#4c1d1d_52%,#064e3b)]"
          />
        )}
        <MuxHoverPreview
          previewUrl={muxPreviewUrl}
          sizes={responsiveImageSizes}
          imageClassName={isVertical ? undefined : "object-left-top"}
          onPreviewLoadedChange={setIsMuxPreviewLoaded}
        />
        <div
          aria-hidden
          data-testid="media-collection-card-text-scrim"
          className={cn(
            "pointer-events-none absolute z-20 rounded-lg opacity-100 transition-opacity duration-300 ease-out",
            isMuxPreviewLoaded &&
              "group-hover:opacity-65 group-focus-visible:opacity-65 group-focus-within:opacity-65",
            isVertical ? "inset-x-0 bottom-0 h-[40%]" : "inset-0",
          )}
          style={textScrimStyle(item, orientation)}
        />
        {showItemNumbers ? (
          <span
            className={cn(
              "absolute top-2 left-2 z-10 leading-none font-bold text-stone-100/90 [text-shadow:0_2px_8px_rgba(0,0,0,0.7)]",
              compactOnMobile ? "text-3xl md:text-5xl" : "text-5xl",
            )}
          >
            {index + 1}
          </span>
        ) : null}
        <WatchProgressBar videoId={item.id} />
        {item.collectionSize ? (
          <div
            className={cn(
              "absolute top-2 right-2 z-10 flex items-center gap-1 rounded bg-black/35 font-semibold text-white backdrop-blur-sm",
              compactOnMobile
                ? "px-1.5 py-0.5 text-xs md:px-2 md:py-1 md:text-sm"
                : "px-2 py-1 text-base sm:text-sm",
            )}
          >
            {item.collectionSize}
          </div>
        ) : null}
        <div
          aria-hidden
          data-testid="media-collection-card-bevel"
          className="pointer-events-none absolute inset-0 z-40 rounded-lg opacity-40 mix-blend-soft-light shadow-[inset_0_0_0_1px_rgba(255,255,255,0.7)]"
        />
        {isInteractive ? (
          <VideoThumbnailInteractionFrame
            data-testid="media-collection-card-hover-outline"
            className="duration-350 ease-[cubic-bezier(0.22,1,0.36,1)]"
          />
        ) : null}
        <VideoThumbnailCaption
          inset={compactOnMobile ? "compact" : "default"}
          className={cn(
            "z-30",
            compactOnMobile
              ? "px-2.5 pt-2.5 pb-3 md:px-4 md:pt-10 md:pb-4"
              : null,
          )}
        >
          {item.label ? (
            <VideoThumbnailEyebrow
              as="div"
              size={compactOnMobile ? "compact-sm" : "regular"}
            >
              {formatLabel(item.label)}
            </VideoThumbnailEyebrow>
          ) : null}
          {item.title ? (
            <VideoThumbnailTitle
              size={
                compactOnMobile
                  ? "compact-sm"
                  : isVertical
                    ? "large"
                    : "prominent"
              }
              className={compactOnMobile ? "md:text-xl" : undefined}
            >
              {item.title}
            </VideoThumbnailTitle>
          ) : null}
        </VideoThumbnailCaption>
      </div>
    </CardFrame>
  )
}

function formatLabel(label: string): string {
  return label.replace(/([a-z0-9])([A-Z])/g, "$1 $2").trim()
}
